// Generated from: doc/design/dashboard/screens/04_email.html
// Layout: hero (H1 "Bandeja unificada" + subtitle counts) + AccountTabs +
//         EmailList + Detail panel (Sheet right) + ArchiveConfirmDialog.
import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { AccountTabs, type AccountTab } from "@/components/email/AccountTabs";
import { EmailList } from "@/components/email/EmailList";
import { EmailDetailPanel } from "@/components/email/EmailDetailPanel";
import { ArchiveConfirmDialog } from "@/components/email/ArchiveConfirmDialog";
import { useUnreadEmailsList } from "@/hooks/useUnreadEmailsList";
import { useArchiveEmail } from "@/hooks/useArchiveEmail";
import { detectProvider } from "@/lib/providers";
import type { EmailItem } from "@/lib/email-types";

// ── Search params ─────────────────────────────────────────────────────────────
const emailSearchSchema = z.object({
  account: z.string().optional(),
  q: z.string().optional(),
  selectedId: z.string().optional(),
});

type EmailSearch = z.infer<typeof emailSearchSchema>;

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildAccountLabel(accountId: string): string {
  if (accountId === "gmail") return "Gmail";
  return accountId;
}

function EmailPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const activeAccount = search.account ?? "all";
  const selectedId = search.selectedId ?? null;

  const { items, isLoading, error, byAccount } = useUnreadEmailsList("all");
  const archiveMutation = useArchiveEmail();

  // IDs currently animating out (180ms ease-out).
  const [archivingIds, setArchivingIds] = useState<ReadonlySet<string>>(new Set());
  // Email pending confirm.
  const [pendingArchive, setPendingArchive] = useState<EmailItem | null>(null);

  // Tabs derived from `byAccount` breakdown — keeps "Todas" at index 0.
  const tabs = useMemo<AccountTab[]>(() => {
    const out: AccountTab[] = [
      { id: "all", label: "Todas", count: byAccount.all ?? 0, provider: null },
    ];
    const accountIds = Object.keys(byAccount).filter((k) => k !== "all").sort();
    for (const accountId of accountIds) {
      out.push({
        id: accountId,
        label: buildAccountLabel(accountId),
        count: byAccount[accountId] ?? 0,
        provider: detectProvider(accountId),
      });
    }
    return out;
  }, [byAccount]);

  // Client-side filter by active tab — no extra request (AC-10.05.02).
  const filteredItems = useMemo(() => {
    if (activeAccount === "all") return items;
    return items.filter((e) => e.account === activeAccount);
  }, [items, activeAccount]);

  const selectedEmail = useMemo(
    () => (selectedId ? items.find((e) => e.id === selectedId) ?? null : null),
    [items, selectedId],
  );

  const totalUnread = byAccount.all ?? 0;
  const accountCount = Object.keys(byAccount).filter(
    (k) => k !== "all" && byAccount[k] >= 0,
  ).length;

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleTabChange = (id: string) => {
    navigate({
      search: (prev: EmailSearch) => ({
        ...prev,
        account: id === "all" ? undefined : id,
      }),
      replace: true,
    });
  };

  const handleSelect = (email: EmailItem) => {
    navigate({
      search: (prev: EmailSearch) => ({ ...prev, selectedId: email.id }),
      replace: true,
    });
  };

  const handleClosePanel = () => {
    navigate({
      search: (prev: EmailSearch) => ({ ...prev, selectedId: undefined }),
      replace: true,
    });
  };

  const handleArchiveRequest = (email: EmailItem) => {
    setPendingArchive(email);
  };

  const handleConfirmArchive = () => {
    if (!pendingArchive) return;
    const target = pendingArchive;
    setPendingArchive(null);

    // Mark as animating-out; remove visually after 180ms then mutate.
    setArchivingIds((prev) => {
      const next = new Set(prev);
      next.add(target.id);
      return next;
    });

    // Close detail if archiving the selected email.
    if (selectedId === target.id) handleClosePanel();

    // Trigger the optimistic mutation immediately — it does the cache update.
    archiveMutation.mutate(
      { id: target.id, account: target.account, message_id: target.message_id },
      {
        onSettled: () => {
          // Clear animation flag once mutation resolves.
          setArchivingIds((prev) => {
            const next = new Set(prev);
            next.delete(target.id);
            return next;
          });
        },
      },
    );
  };

  const handleOpenExternal = (email: EmailItem) => {
    window.open(email.web_url, "_blank", "noopener,noreferrer");
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <section className="flex flex-col">
      {/* Hero v2 */}
      <header className="px-1 pb-6">
        <h1 className="text-lg font-semibold tracking-tight text-fg leading-tight">
          Email
        </h1>
        <p className="mt-1 text-sm text-fg-secondary tabular">
          {isLoading
            ? "Cargando…"
            : `${totalUnread} sin leer · ${accountCount} cuentas conectadas`}
        </p>
      </header>

      {/* Tabs */}
      <AccountTabs
        tabs={tabs}
        activeId={activeAccount}
        onChange={handleTabChange}
      />

      {/* List */}
      <div className="mt-2">
        {error ? (
          <div className="px-6 py-12 text-center text-sm text-danger">
            Error al cargar emails: {error.message}
          </div>
        ) : isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-fg-tertiary">
            Cargando emails…
          </div>
        ) : (
          <EmailList
            emails={filteredItems}
            selectedId={selectedId}
            archivingIds={archivingIds}
            onSelect={handleSelect}
            onArchive={handleArchiveRequest}
            onOpenExternal={handleOpenExternal}
          />
        )}
      </div>

      {/* Detail panel (Sheet right) */}
      <EmailDetailPanel
        email={selectedEmail}
        open={Boolean(selectedEmail)}
        onClose={handleClosePanel}
        onArchive={handleArchiveRequest}
      />

      {/* Archive confirm dialog */}
      <ArchiveConfirmDialog
        open={Boolean(pendingArchive)}
        provider={pendingArchive?.provider}
        onCancel={() => setPendingArchive(null)}
        onConfirm={handleConfirmArchive}
      />
    </section>
  );
}

export const Route = createFileRoute("/_authenticated/email")({
  validateSearch: emailSearchSchema,
  component: EmailPage,
});
